/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List, Map as ImmutableMap, fromJS } from 'immutable';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReportingActionsLoader from 'reporting-action-components/reportingActions/AsyncReportingActionsLoader';
import SaveReportButton from 'reporting-action-components/reportingActions/saveReport/SaveReportButton';
import { CRM_OBJECT } from 'reporting-data/constants/dataTypes';
import getProperties from 'reporting-data/properties';
import { groupByColumn } from 'reporting-snowflake/relational/enhance/drilldown/group-by';
import ExportReportButton from 'reporting-action-components/reportingActions/exportReport/ExportReportButton';
import ErrorBoundaryDecorator from 'reporting-ui-components/reports/decorators/ErrorBoundaryDecorator';
import memoize from 'transmute/memoize';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Box } from 'foundations-components/transitional/components/Box';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { SALES_ANALYTICS_WIDGETS } from '../customWidgets/lib/salesExplorer';
import Widgets from '../customWidgets/constants/widgets';
import { customWidgetTypeIs, customWidgetTypeIncludes } from '../customWidgets/utils';
import { createDefaultDrilldownReportOptions } from '../snowflake/report-options';
import { buildDrilldownReport } from './buildDrilldownReport';
import DrilldownDimensionHandler, { SnowflakeDimensionHandler, getDataTypeLabel } from './DrilldownDimensionHandler';
import { DRILLDOWN_BACKTRACK, DRILLDOWN_OPENED, DRILLDOWN_SAVE_REPORT_COMPLETED } from './DrilldownEvents';
import ReportContent from './ReportContent';
import { useDrilldownRecordCount } from './useDrilldownRecordCount';
import { useEnhancedReport } from './useEnhancedReport';
import { isImmutableJourneyReport } from 'reporting-snowflake/funnel/types/journeyReport';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { useDeps } from '../snowflake/useDeps';
import { AsyncStatus } from 'reporting-data/asyncData/AsyncStatus';
import { isLoading, isFailed } from 'reporting-data/asyncData/AsyncData';
import ReportErrored from '../../reports/inferLogic/components/states/ReportErrored';
import { WithUserInfoProvider } from './UserInfoProvider';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { isFieldExpressionBased } from 'reporting-snowflake/relational/utils/column-utils';
import { isDatePartExpressionFieldAlias } from 'reporting-snowflake/relational/enhance/drilldown/drilldown-utils';
import { WithFiscalYearProvider } from './FiscalYearProvider';
import { ExportToListButton } from '../../contrib/ExportToListButton';
import ReportingPluginsProvider from 'reporting-plugins/ReportingPluginsProvider';
import { isConfigReport, isHistoricalReport, isReportWithPlugins, isUnifiedEventsReport, isUnifiedEventsQLReport } from 'reporting-data/tsTypes/reportTypes';
import { getDataType, getDimensions, isSearchReport, getRawDataType, getObjectTypeId, getMetrics, getConfigType } from 'reporting-data/report/configReportGetters';
import { getReportDefinition } from 'reporting-data/report/reportGetters';
import { Alert } from 'foundations-components/transitional/components/Alert';
import useCloneTracker from '../../lib/useCloneTracker';
import { SUM } from 'reporting-data/constants/metricTypes';
import { AGGREGATION, TIME_SERIES } from 'reporting-data/constants/configTypes';
import { View } from 'foundations-components/components/View';
import { useIsCrmObjectTableSupported } from './CrmObjectTable/useIsCrmObjectTableSupported';
import { useCrmObjectTableReportComponent } from './CrmObjectTable/ProvideCrmObjectTableReportComponent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TABLE_LIMIT = 10;
const DRILLDOWN_MAX_LEVEL = 5;
const noop = () => {};
const countMetric = fromJS({
  property: 'count',
  metricTypes: [SUM]
});
const getTableReport = memoize((report, baseReport, dimension, dimensionProperty, onDrilldown) => {
  const modifiedReport = report.setIn(['config', 'limit'], TABLE_LIMIT).setIn(['displayParams', 'onDrilldown'], onDrilldown);
  if (isUnifiedEventsReport(report)) {
    return modifiedReport.setIn(['config', 'dimensions', 0], dimension);
  }
  if (isSearchReport(report) && dimension) {
    const baseReportConfigType = isConfigReport(baseReport) && getConfigType(baseReport);
    const metrics = [AGGREGATION, TIME_SERIES].includes(baseReportConfigType) ? getMetrics(baseReport) : List([countMetric]);
    return buildDrilldownReport(modifiedReport, dimension, dimensionProperty, metrics);
  }
  return modifiedReport;
});
const canGroupData = (baseReport, report) => {
  return !customWidgetTypeIncludes(report, SALES_ANALYTICS_WIDGETS) && !customWidgetTypeIs(baseReport, Widgets.DEAL_FORECAST) && !isUnifiedEventsQLReport(report);
};
function StandardDrilldownReport({
  appUsageTracker,
  baseReport,
  report,
  drilldownEventCallback,
  onDrilldown,
  onDrilldownSave,
  onExportListClick,
  onExportListSubmit,
  onCustomExportButtonClick,
  customSaveReportButtonProps,
  customExportReportButtonProps,
  hideExportButton,
  goToCrmLink,
  errorRenderer,
  resolve,
  visualize,
  trackingProperties
}) {
  var _goToCrmLink$href;
  const dataType = getDataType(report);
  const objectTypeId = getObjectTypeId(report);
  const firstDimension = getDimensions(report).get(0);
  const [dimension, setDimension] = useState(isUnifiedEventsReport(report) ? firstDimension : null);
  const recordCount = useDrilldownRecordCount(report);
  const [stateRecordCount, setStateRecordCount] = useState(null);
  const [reportProperties, setReportProperties] = useState(ImmutableMap());
  const canSaveDrilldown = !customWidgetTypeIncludes(report, SALES_ANALYTICS_WIDGETS) && !isHistoricalReport(report);
  useEffect(() => {
    if (isUnifiedEventsReport(report)) {
      setDimension(firstDimension);
    } else {
      setDimension(null);
    }
  }, [report, firstDimension]);
  useEffect(() => {
    getProperties(dataType).then(properties => setReportProperties(properties.get(dataType)));
  }, [dataType]);
  const tableReport = getTableReport(report, baseReport, dimension, reportProperties.find(property => property.get('name') === dimension) || ImmutableMap(), onDrilldown);
  const asyncEnhancedReport = useEnhancedReport({
    data: tableReport,
    status: AsyncStatus.SUCCEEDED
  });
  const isCrmObjectTableSupported = useIsCrmObjectTableSupported({
    tableReport
  });
  const CrmObjectTableComponent = useCrmObjectTableReportComponent();
  if (isLoading(asyncEnhancedReport)) {
    return /*#__PURE__*/_jsx(LoadingDots, {});
  }
  if (isFailed(asyncEnhancedReport)) {
    return /*#__PURE__*/_jsx(ReportErrored, {
      error: asyncEnhancedReport.error
    });
  }
  const {
    data: enhancedReport
  } = asyncEnhancedReport;
  const showDimensionHandlerForSearchReport = isSearchReport(report) && Boolean(stateRecordCount || recordCount);
  const showDimensionHandlerForUnifiedEventReport = isUnifiedEventsReport(report);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(TFlex, {
      "data-test-id": "drilldown-modal-opened",
      justify: "between",
      paddingBlockEnd: "400",
      paddingInline: "0",
      children: [showDimensionHandlerForSearchReport || showDimensionHandlerForUnifiedEventReport ? /*#__PURE__*/_jsx(DrilldownDimensionHandler, {
        dataType: getRawDataType(report),
        objectTypeId: objectTypeId,
        dimension: dimension,
        drilldownEventCallback: drilldownEventCallback,
        recordCount: stateRecordCount || recordCount,
        setDimension: setDimension,
        canGroupData: canGroupData(baseReport, report),
        report: report
      }) : /*#__PURE__*/_jsx("div", {}), /*#__PURE__*/_jsx(Box, {
        alignSelf: "center",
        shrink: 0,
        children: /*#__PURE__*/_jsxs(ReportingActionsLoader, {
          children: [canSaveDrilldown && /*#__PURE__*/_jsx(ExportToListButton, {
            onExportListClick: onExportListClick,
            onExportListSubmit: onExportListSubmit,
            report: enhancedReport
          }), goToCrmLink && /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            size: "small",
            href: (_goToCrmLink$href = goToCrmLink === null || goToCrmLink === void 0 ? void 0 : goToCrmLink.href) !== null && _goToCrmLink$href !== void 0 ? _goToCrmLink$href : goToCrmLink,
            external: true,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reporting-ui-components.drilldownViewer.goToCRMLink"
            })
          }), !hideExportButton && (onCustomExportButtonClick ? /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            size: "small",
            onClick: onCustomExportButtonClick,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reporting-ui-components.export.action"
            })
          }) : /*#__PURE__*/_jsx(ExportReportButton, Object.assign({
            appUsageTracker: appUsageTracker,
            use: "tertiary-light",
            size: "small",
            report: enhancedReport,
            trackingProperties: trackingProperties
          }, customExportReportButtonProps))), !canSaveDrilldown ? null : customSaveReportButtonProps ? /*#__PURE__*/_jsx(Button, Object.assign({
            size: "small",
            use: "tertiary-light"
          }, customSaveReportButtonProps)) : /*#__PURE__*/_jsx(SaveReportButton, {
            appUsageTracker: appUsageTracker,
            "data-test-id": "drilldown-save",
            isDrilldown: true,
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "reporting-ui-components.drilldownViewer.saveButton"
            }),
            onSaveSuccess: onDrilldownSave,
            report: enhancedReport.delete('id"'),
            size: "small",
            use: "tertiary-light",
            trackingProperties: trackingProperties
          })]
        })
      })]
    }), isCrmObjectTableSupported && CrmObjectTableComponent ? /*#__PURE__*/_jsx(CrmObjectTableComponent, {
      tableReport: tableReport
    }) : /*#__PURE__*/_jsx(ReportContent, {
      report: enhancedReport,
      setRecordCount: setStateRecordCount,
      drilldownEventCallback: drilldownEventCallback,
      onDrilldown: onDrilldown,
      errorRenderer: errorRenderer,
      resolve: resolve,
      visualize: visualize
    })]
  });
}
const NoFunctionAccessDrilldownSaveDisabled = () => {
  const {
    openUpgradeModal
  } = useUpgradeModal('advanced-builder-drilldown-formula-access-modal');
  return /*#__PURE__*/_jsx(Tooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: 'reporting-ui-components.drilldownViewer.saveDisabled.snowflakeDateComparisonDrilldown'
    }),
    children: /*#__PURE__*/_jsxs(Button, {
      onClick: openUpgradeModal,
      use: "tertiary-light",
      size: "sm",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-ui-components.drilldownViewer.saveButton"
      }), /*#__PURE__*/_jsx(LockBadge, {})]
    })
  });
};
function SnowflakeDrilldownReport({
  appUsageTracker,
  report,
  drilldownEventCallback,
  onDrilldown,
  onDrilldownSave,
  customExportReportButtonProps,
  hideExportButton
}) {
  const [columnIdToGroupBy, setColumnIdToGroupBy] = useState(null);
  useEffect(() => setColumnIdToGroupBy(null), [report]);
  const {
    user: {
      scopes
    }
  } = useContext(UserInfoContext);
  const hasDatasetsAccess = scopes.includes('reporting-datasets-access');
  const reportDefinitionBeforeGrouping = getReportDefinition(report);
  const {
    data: deps
  } = useDeps(reportDefinitionBeforeGrouping);
  const [ungroupedRecordCount, setUngroupedRecordCount] = useState(false);
  const handleInitialUngroupedDatasetLoadCompleted = dataset => {
    if (!ungroupedRecordCount && dataset) {
      setUngroupedRecordCount(dataset.paginationSize);
    }
  };
  const hasEnhancements = columnIdToGroupBy != null;
  const enhancedReport = hasEnhancements && !!deps ? report.update('reportDefinition', reportDefinition => groupByColumn(reportDefinition, columnIdToGroupBy, deps)) : undefined;
  const reportToRender = hasEnhancements ? enhancedReport : report;
  if (!reportToRender) {
    return /*#__PURE__*/_jsx(LoadingDots, {});
  }
  const shouldRenderGroupBy = Boolean(ungroupedRecordCount);
  const hasDatePartExpressionFilter = reportToRender.getIn(['reportDefinition', 'filtering', 'groups'], List()).some(group => group.get('filters', List()).some(filter => isFieldExpressionBased(filter.field) && isDatePartExpressionFieldAlias(filter.field.name)));
  const canSaveDrilldown = hasDatePartExpressionFilter ? hasDatasetsAccess : true;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(TFlex, {
      "data-test-id": "drilldown-modal-opened",
      paddingBlockEnd: "400",
      paddingInline: "0",
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 1,
        children: shouldRenderGroupBy && /*#__PURE__*/_jsx(SnowflakeDimensionHandler, {
          reportDefinition: reportDefinitionBeforeGrouping,
          dimension: columnIdToGroupBy,
          setDimension: setColumnIdToGroupBy,
          drilldownEventCallback: drilldownEventCallback,
          recordCount: ungroupedRecordCount
        })
      }), /*#__PURE__*/_jsx(Box, {
        children: /*#__PURE__*/_jsx(TFlex, {
          justify: "end",
          children: /*#__PURE__*/_jsxs(ReportingActionsLoader, {
            children: [!hideExportButton && /*#__PURE__*/_jsx(ExportReportButton, Object.assign({
              appUsageTracker: appUsageTracker,
              use: "tertiary-light",
              size: "small",
              report: reportToRender
            }, customExportReportButtonProps)), canSaveDrilldown ? /*#__PURE__*/_jsx(SaveReportButton, {
              appUsageTracker: appUsageTracker,
              "data-test-id": "drilldown-save",
              isDrilldown: true,
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "reporting-ui-components.drilldownViewer.saveButton"
              }),
              onSaveSuccess: onDrilldownSave,
              report: reportToRender,
              size: "small",
              use: "tertiary-light"
            }) : /*#__PURE__*/_jsx(NoFunctionAccessDrilldownSaveDisabled, {})]
          })
        })
      })]
    }), /*#__PURE__*/_jsx(ReportContent, {
      report: reportToRender,
      initialReportOptions: createDefaultDrilldownReportOptions(getReportDefinition(reportToRender)),
      onDrilldown: onDrilldown,
      drilldownEventCallback: drilldownEventCallback,
      flushSortsOnDrilldown: true,
      onReportResolve: handleInitialUngroupedDatasetLoadCompleted
    })]
  });
}
function JourneyDrilldownReport({
  appUsageTracker,
  report,
  drilldownEventCallback,
  onDrilldown,
  onExportListClick,
  onExportListSubmit
}) {
  const [recordInfo, setRecordInfo] = useState();
  const handleDrilldownResolved = resolvedInfo => {
    if (!recordInfo && resolvedInfo) {
      getDataTypeLabel(CRM_OBJECT, report.getIn(['journeyQuery', 'objectQuery', 'table', 'objectTypeId'])).then(label => setRecordInfo({
        recordCount: resolvedInfo.getIn(['resolveDatamap', 'primary', 'paginationSize']) || resolvedInfo.getIn(['resolveDatamap', 'primary', 'data'], List()).count(),
        objectText: label
      }));
    }
  };
  const hasAnonymousVisitors = report.getIn(['journeyQuery', 'allowAnonymousVisitors'], null);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(TFlex, {
      "data-test-id": "drilldown-modal-opened",
      paddingBlockEnd: "400",
      paddingInline: "0",
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 1,
        children: recordInfo && /*#__PURE__*/_jsx(View, {
          marginBlock: "200",
          as: "div",
          children: /*#__PURE__*/_jsxs(View, {
            marginInlineEnd: "300",
            as: "span",
            children: [/*#__PURE__*/_jsx(FormattedNumber, {
              value: recordInfo.recordCount
            }), ' ', recordInfo.objectText]
          })
        })
      }), /*#__PURE__*/_jsx(Box, {
        children: /*#__PURE__*/_jsx(TFlex, {
          justify: "end",
          children: /*#__PURE__*/_jsxs(ReportingActionsLoader, {
            children: [/*#__PURE__*/_jsx(ExportToListButton, {
              onExportListClick: onExportListClick,
              onExportListSubmit: onExportListSubmit,
              report: report,
              extraInfo: hasAnonymousVisitors ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "reporting-ui-components.export.listModal.anonymousVisitors"
              }) : undefined
            }), /*#__PURE__*/_jsx(ExportReportButton, {
              appUsageTracker: appUsageTracker,
              use: "tertiary-light",
              size: "small",
              report: report
            })]
          })
        })
      })]
    }), /*#__PURE__*/_jsx(ReportContent, {
      report: report,
      onDrilldown: onDrilldown,
      drilldownEventCallback: drilldownEventCallback,
      flushSortsOnDrilldown: true,
      onReportResolve: handleDrilldownResolved
    })]
  });
}
const DrilldownReportWithPlugins = ReportingPluginsProvider(StandardDrilldownReport);
function DrilldownReport({
  appUsageTracker,
  baseReport,
  report,
  drilldownEventCallback,
  onDrilldown,
  onExportListClick,
  onExportListSubmit,
  onCustomExportButtonClick,
  customSaveReportButtonProps,
  customExportReportButtonProps,
  hideExportButton,
  goToCrmLink,
  errorRenderer,
  trackingProperties
}) {
  const hasReportDefinition = report.has('reportDefinition');
  const isJourneyReport = isImmutableJourneyReport(report);
  const onDrilldownSave = ({
    dashboard,
    report: savedReport
  }) => drilldownEventCallback(DRILLDOWN_SAVE_REPORT_COMPLETED, {
    dashboard,
    report: savedReport
  });
  if (hasReportDefinition) {
    return /*#__PURE__*/_jsx(SnowflakeDrilldownReport, {
      appUsageTracker: appUsageTracker,
      report: report,
      drilldownEventCallback: drilldownEventCallback,
      onDrilldown: onDrilldown,
      onDrilldownSave: onDrilldownSave,
      customExportReportButtonProps: customExportReportButtonProps,
      hideExportButton: hideExportButton
    });
  } else if (isJourneyReport) {
    return /*#__PURE__*/_jsx(JourneyDrilldownReport, {
      appUsageTracker: appUsageTracker,
      report: report,
      drilldownEventCallback: drilldownEventCallback,
      onDrilldown: onDrilldown,
      onExportListClick: onExportListClick,
      onExportListSubmit: onExportListSubmit
    });
  } else if (isReportWithPlugins(report)) {
    return /*#__PURE__*/_jsx(DrilldownReportWithPlugins, {
      appUsageTracker: appUsageTracker,
      baseReport: baseReport,
      report: report,
      drilldownEventCallback: drilldownEventCallback,
      onDrilldown: onDrilldown,
      onDrilldownSave: onDrilldownSave,
      onExportListClick: onExportListClick,
      onExportListSubmit: onExportListSubmit,
      onCustomExportButtonClick: onCustomExportButtonClick,
      customExportReportButtonProps: customExportReportButtonProps,
      customSaveReportButtonProps: customSaveReportButtonProps,
      hideExportButton: hideExportButton,
      goToCrmLink: goToCrmLink,
      errorRenderer: errorRenderer,
      trackingProperties: trackingProperties
    });
  } else {
    return /*#__PURE__*/_jsx(StandardDrilldownReport, {
      appUsageTracker: appUsageTracker,
      baseReport: baseReport,
      report: report,
      drilldownEventCallback: drilldownEventCallback,
      onDrilldown: onDrilldown,
      onDrilldownSave: onDrilldownSave,
      onExportListClick: onExportListClick,
      onExportListSubmit: onExportListSubmit,
      onCustomExportButtonClick: onCustomExportButtonClick,
      customExportReportButtonProps: customExportReportButtonProps,
      customSaveReportButtonProps: customSaveReportButtonProps,
      hideExportButton: hideExportButton,
      goToCrmLink: goToCrmLink,
      errorRenderer: errorRenderer,
      trackingProperties: trackingProperties
    });
  }
}
const WrappedDrilldownReport = ErrorBoundaryDecorator(WithUserInfoProvider(WithFiscalYearProvider(DrilldownReport)));
export function DrilldownViewer({
  appUsageTracker,
  baseReport,
  drilldownEventCallback = noop,
  report,
  onClose,
  onError,
  onExportListClick,
  onExportListSubmit,
  onCustomExportButtonClick,
  customSaveReportButtonProps,
  customExportReportButtonProps,
  hideExportButton = false,
  drilldownDescription,
  drilldownAlert,
  goToCrmLink,
  errorRenderer,
  trackingProperties
}) {
  const [drilldowns, setDrilldowns] = useState(List([report]));
  const getOnDrilldown = useCallback(index => index < DRILLDOWN_MAX_LEVEL ? newDrill => {
    drilldownEventCallback(DRILLDOWN_OPENED, {
      level: index + 1,
      report: newDrill
    });
    setDrilldowns(list => list.push(newDrill));
  } : null, [drilldownEventCallback]);
  const drillableReport = useMemo(() => {
    return drilldowns.isEmpty() ? undefined : drilldowns.last().deleteIn(['displayParams', 'onDrilldown']);
  }, [drilldowns]);
  const reportingTracker = useCloneTracker(appUsageTracker);
  if (drilldowns.isEmpty()) {
    return /*#__PURE__*/_jsx(LoadingDots, {});
  }
  const Description = drilldownDescription || /*#__PURE__*/_jsxs(Breadcrumbs, {
    flush: true,
    marginBlockStart: "0",
    display: "flex",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx("span", {
      children: baseReport.get('name') || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-ui-components.drilldownViewer.defaultReportName"
      })
    }), drilldowns.pop().map((drill, index) => /*#__PURE__*/_jsx(Button, {
      use: "link",
      onClick: () => {
        drilldownEventCallback(DRILLDOWN_BACKTRACK, {
          level: index + 1
        });
        setDrilldowns(drilldowns.take(index + 1));
      },
      children: drill.get('name')
    }, index)), !drilldowns.isEmpty() && /*#__PURE__*/_jsx("span", {
      children: drilldowns.last().get('name')
    })]
  });
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "drilldown-modal",
    "data-draggable": "false",
    width: "90%",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose,
        "data-test-id": "drilldown-close-button"
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.drilldownViewer.modalTitle"
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      paddingBlockEnd: "800",
      children: [drilldownAlert, Description, report.get('flpRestricted') && /*#__PURE__*/_jsx(Alert, {
        titleText: I18n.text('reporting-ui-components.drilldownViewer.hasFlpRestrictedPropertyBanner.title'),
        type: "warning",
        marginBlockEnd: "200",
        children: I18n.text('reporting-ui-components.drilldownViewer.hasFlpRestrictedPropertyBanner.message')
      }), /*#__PURE__*/_jsx(WrappedDrilldownReport, {
        appUsageTracker: reportingTracker,
        baseReport: baseReport,
        report: drillableReport,
        drilldownLevel: drilldowns.count(),
        drilldownEventCallback: drilldownEventCallback,
        onDrilldown: getOnDrilldown(drilldowns.count()),
        onError: onError,
        onExportListClick: onExportListClick,
        onExportListSubmit: onExportListSubmit,
        onCustomExportButtonClick: onCustomExportButtonClick,
        customSaveReportButtonProps: customSaveReportButtonProps,
        customExportReportButtonProps: customExportReportButtonProps,
        hideExportButton: hideExportButton,
        goToCrmLink: goToCrmLink,
        errorRenderer: errorRenderer,
        trackingProperties: trackingProperties
      })]
    })]
  });
}